import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, X, Sparkles, HelpCircle } from 'lucide-react';

export interface TourStep {
  target: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface ProductTourProps {
  steps: TourStep[];
  tourKey: string;
  onComplete?: () => void;
}

export const ProductTour: React.FC<ProductTourProps> = ({ steps, tourKey, onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const tooltipRef = useRef<HTMLDivElement>(null);

  // 1. Check if the tour has been completed before
  useEffect(() => {
    // Disable tour auto-start on mobile screens
    if (window.innerWidth < 768) return;

    const isCompleted = localStorage.getItem(`completed_tour_${tourKey}`);
    if (!isCompleted) {
      // Small delay before welcome popup appears
      const timer = setTimeout(() => {
        setIsOpen(true);
        setCurrentStep(0);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [tourKey]);

  // Handle listening for global manual restarts of the tour
  useEffect(() => {
    const handleRestart = (e: Event) => {
      // Disable tour restart on mobile screens
      if (window.innerWidth < 768) return;

      const customEvent = e as CustomEvent;
      if (customEvent.detail === tourKey) {
        setIsOpen(true);
        setCurrentStep(0);
      }
    };
    window.addEventListener('restart_tour', handleRestart);
    return () => window.removeEventListener('restart_tour', handleRestart);
  }, [tourKey]);

  // 2. Track screen resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowSize({ width, height: window.innerHeight });
      // Close active tour if screen is resized below the mobile threshold
      if (width < 768) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 3. Calculate target element bounding box
  useEffect(() => {
    if (!isOpen || steps.length === 0) return;

    const step = steps[currentStep];
    const element = step.target && step.target !== 'body' && step.position !== 'center'
      ? (document.querySelector(step.target) as HTMLElement)
      : null;

    if (element) {
      // Highlight the element
      element.classList.add('tour-highlight');

      // Save original styles to restore them on clean-up
      const originalZIndex = element.style.zIndex;
      const originalPointerEvents = element.style.pointerEvents;
      const originalPosition = element.style.position;

      // Make element fully clickable above the backdrop overlay
      element.style.zIndex = '10001';
      element.style.pointerEvents = 'auto';

      // Ensure z-index works by relative positioning static elements
      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.position === 'static') {
        element.style.position = 'relative';
      }

      const updateCoords = () => {
        const rect = element.getBoundingClientRect();
        // Add slight padding around the highlighted area
        const padding = 6;
        setCoords({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });

        // Scroll element into view smoothly if out of viewport
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      };

      updateCoords();

      // Recalculate slightly after scroll/render
      const timer = setTimeout(updateCoords, 100);

      return () => {
        clearTimeout(timer);
        element.classList.remove('tour-highlight');
        element.style.zIndex = originalZIndex;
        element.style.pointerEvents = originalPointerEvents;
        element.style.position = originalPosition;
      };
    } else {
      // Element not found on this page yet, render center tooltip card
      setCoords(null);
    }
  }, [isOpen, currentStep, steps, windowSize]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  const handleFinish = () => {
    setIsOpen(false);
    localStorage.setItem(`completed_tour_${tourKey}`, 'true');
    onComplete?.();
  };

  if (!isOpen || windowSize.width < 768) return null;

  const current = steps[currentStep];
  const position = current?.position || 'bottom';

  // SVG Mask calculations (EvenOdd rule draws backdrop and punch cutout)
  const maskPath = coords 
    ? `M 0 0 h ${windowSize.width} v ${windowSize.height} h ${-windowSize.width} z M ${coords.left} ${coords.top} v ${coords.height} h ${coords.width} v ${-coords.height} z`
    : `M 0 0 h ${windowSize.width} v ${windowSize.height} h ${-windowSize.width} z`;

  // Custom positioning styles for the tooltip card
  const getTooltipStyle = () => {
    if (!coords) {
      // Center position if no element coordinates
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed' as const,
      };
    }

    const gap = 16;
    const tooltipWidth = 320;
    
    let top = coords.top + coords.height + gap;
    let left = coords.left + coords.width / 2 - tooltipWidth / 2;

    if (position === 'top') {
      top = coords.top - 200 - gap; // fallback approximate height
      left = coords.left + coords.width / 2 - tooltipWidth / 2;
    } else if (position === 'left') {
      left = coords.left - tooltipWidth - gap;
      top = coords.top + coords.height / 2 - 80;
    } else if (position === 'right') {
      left = coords.left + coords.width + gap;
      top = coords.top + coords.height / 2 - 80;
    }

    // Boundary constraints (prevent card from going offscreen)
    left = Math.max(16, Math.min(windowSize.width - tooltipWidth - 16, left));
    // Use 340 to ensure even a tall card stays fully within viewport
    top = Math.max(16, Math.min(windowSize.height - 340, top));

    return {
      top: `${top}px`,
      left: `${left}px`,
      position: 'fixed' as const,
    };
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden select-none" style={{ pointerEvents: 'none' }}>
      
      {/* SVG Backdrop Mask cutout with EvenOdd rule */}
      {/* pointer-events: none on SVG so the path handles it; visibleFill means only filled (dark) parts catch clicks, hole passes through */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <path 
          d={maskPath} 
          fill="rgba(0, 0, 0, 0.78)" 
          fillRule="evenodd" 
          style={{ pointerEvents: 'visibleFill' }}
          className="transition-all duration-300 ease-out"
        />
      </svg>

      {/* Target Focus Border Overlay */}
      <AnimatePresence>
        {coords && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'absolute',
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              height: `${coords.height}px`,
            }}
            className="border-2 border-red-500 rounded-xl pointer-events-none shadow-[0_0_20px_rgba(239,68,68,0.4)] transition-all duration-300 ease-out"
          />
        )}
      </AnimatePresence>

      {/* Tooltip Card - re-enable pointer events so it stays interactive */}
      <motion.div
        ref={tooltipRef}
        initial={{ opacity: 0, scale: 0.95, y: coords ? 0 : 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{ ...getTooltipStyle(), pointerEvents: 'auto' }}
        className="w-[320px] bg-[#0E0E0E]/95 border border-neutral-800/80 rounded-3xl p-6 shadow-2xl backdrop-blur-md flex flex-col gap-4 text-white z-[10000] transition-all duration-300 ease-out"
      >
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-red-500" />
            <h4 className="text-xs font-black uppercase tracking-widest text-neutral-400">Trading Guide</h4>
          </div>
          <button 
            onClick={handleSkip}
            className="p-1 hover:bg-neutral-900 border border-neutral-900 rounded-lg text-neutral-500 hover:text-white transition-all outline-none"
          >
            <X size={14} />
          </button>
        </div>

        <div className="space-y-1">
          <h3 className="text-sm font-black text-white leading-tight uppercase tracking-tight">
            {current?.title || "Selamat Datang di Catat-Crypto!"}
          </h3>
          <p className="text-xs text-neutral-400 leading-relaxed font-semibold">
            {current?.content || "Kami telah menyusun panduan cepat untuk mengenalkan area navigasi dan fitur trading platform ini."}
          </p>
        </div>

        {/* Footer controls */}
        <div className="flex items-center justify-between border-t border-neutral-900/60 pt-4 mt-1 select-none">
          {/* Progress bullets */}
          <span className="text-[10px] font-black font-mono text-neutral-600 uppercase">
            Step {currentStep + 1} / {steps.length}
          </span>

          <div className="flex gap-2">
            {currentStep > 0 && (
              <button 
                onClick={handleBack}
                className="p-2 bg-neutral-900 border border-neutral-800 rounded-xl hover:text-white transition-all outline-none flex items-center justify-center"
              >
                <ChevronLeft size={14} />
              </button>
            )}
            
            <button 
              onClick={handleNext}
              className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-xl text-[10px] font-black text-white uppercase tracking-wider transition-all flex items-center gap-1 shadow active:scale-[0.98] outline-none"
            >
              {currentStep === steps.length - 1 ? 'Selesai' : 'Lanjut'}
              {currentStep < steps.length - 1 && <ChevronRight size={10} />}
            </button>
          </div>
        </div>
      </motion.div>

    </div>
  );
};
